import PopupBasicLazy from '../components/popups/PopupBasicLazy';
import AssetGroup from '@play-co/timestep-core/lib/ui/resource/AssetGroup';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import {
  ImageSliceView,
  ImageView,
  MovieClip,
} from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import type { InviteRewardType } from 'src/replicant/social/social.ruleset';
import { Feature } from 'src/replicant/analytics';
import { LangBitmapFontTextShadowView } from '../../lib/ui/components/LangBitmapFontTextShadowView';
import { assertNever } from 'src/replicant/utils';
import { PartialRecord } from 'src/replicant/utils/types';
import { animateEnergy, animateGems } from '../animation/CurrencyAnimation';

const assets = new AssetGroup([
  'assets/ui/friends/banner_friend_joined.png',
  {
    url: 'assets/ui/match2/movieclips/level_complete_stars',
    loadMethod: MovieClip.animationLoader,
  },
]);

export default class PopupFriendJoinedReward extends PopupBasicLazy {
  private rewardLabel: LangBitmapFontTextView;
  private rewardFrame: ImageSliceView;
  private rewardIcon!: ImageScaleView;
  private reward!: { type: InviteRewardType; value: number };

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: opts.close,
      zIndex: 10000,
      width: uiConfig.popups.minimumWidth,
      height: 410,
      skipTitle: true,
      tracking: {
        feature: Feature.INVITE._,
        $subFeature: Feature.INVITE.INVITE_FRIEND_JOINED_TO_GAME,
      },
    });

    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      width: this.box.style.width - 80,
      height: 80,
      x: 40,
      y: 68,
      zIndex: 1,
    });

    this.box.addSubview(
      new ImageView({
        image: 'assets/ui/friends/banner_friend_joined.png',
        width: 425,
        height: 209,
        x: this.box.style.width / 2,
        y: -42,
        centerOnOrigin: true,
      }),
    );

    this.rewardFrame = this.box.addSubview(
      new ImageSliceView({
        image: 'assets/ui/shared/frames/inset_panel.png',
        slices: {
          left: 25,
          right: 25,
          top: 25,
          bot: 25,
        },
        centerOnOrigin: true,
        x: this.box.style.width / 2,
        y: 212,
        height: 88,
        width: 300,
      }),
    );

    this.rewardLabel = new LangBitmapFontTextShadowView({
      size: 38,
      font: 'Body',
      height: 68,
      width: 136,
      verticalAlign: 'center',
      align: 'center',
      x: 120,
      y: 8,
    });

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...uiConfig.buttons.secondary,
        labelOffsetX: -4,
        labelOffsetY: -1,
        fontSize: 38,
        font: bitmapFonts('Body'),
        x: this.box.style.width / 2,
        y: this.box.style.height - 35 - 80 / 2,
        width: 229,
        height: 80,
        centerOnOrigin: true,
        localeText: () => i18n('basic.ok'),
        onClick: async () => {
          sounds.playSound('ok');
          await this.animateReward();
          opts.close();
        },
        tracking: {
          feature: Feature.INVITE._,
          $subFeature: Feature.INVITE.INVITE_FRIEND_JOINED_TO_GAME,
        },
      }),
    );
  }

  public init(reward: PartialRecord<InviteRewardType, number>) {
    super.init({
      message: i18n('social.invite.friendJoinedReward.subtitle'),
    });

    new MovieClip({
      superview: this.root,
      url: 'assets/ui/match2/movieclips/level_complete_stars',
      x: this.box.style.x,
      y: this.box.style.y,
    }).play('staranim_confetti');
    sounds.playSound('applause');

    const [type, value] =
      Object.entries(reward).find(([_, value]) => value > 0) ?? [];
    if (!type || !value) return;

    const rewardType = type as InviteRewardType;
    this.reward = { type: rewardType, value };

    // Update reward type icon
    this.rewardFrame.removeAllSubviews();
    this.rewardFrame.addSubview(this.rewardLabel);
    this.rewardIcon = this.rewardFrame.addSubview(
      this.getRewardIcon(rewardType),
    );

    // Set reward value
    this.rewardLabel.localeText = () => `x ${parseAmount(value ?? 0)}`;
  }

  /**
   * Get reward icon
   */
  private getRewardIcon(type: InviteRewardType) {
    switch (type) {
      case 'gems':
        return new ImageScaleView({
          scaleMethod: 'contain',
          image: 'assets/ui/hud/icons/hud_gem.png',
          width: 74,
          height: 74,
          x: 60,
          y: 7,
        });
      case 'energy':
        return new ImageScaleView({
          scaleMethod: 'contain',
          image: 'assets/ui/hud/icons/hud_energy.png',
          width: 74,
          height: 74,
          x: 60,
          y: 7,
        });
      case 'moves':
        return new ImageScaleView({
          scaleMethod: 'contain',
          image: 'assets/ui/hud/icons/hud_energy.png',
          width: 74,
          height: 74,
          x: 60,
          y: 7,
        });
      default:
        assertNever(type);
    }
  }

  protected getAssetsGroup() {
    return assets;
  }

  private async animateReward() {
    const iconPos = this.rewardIcon.getPosition(this.root);
    const start = {
      x: iconPos.x + this.rewardIcon.style.width / 2,
      y: iconPos.y + this.rewardIcon.style.height / 2,
    };

    switch (this.reward.type) {
      case 'gems':
        await animateGems({
          superview: this.root,
          start: { position: start, scale: 1 },
          count: this.reward.value,
        });
        break;
      case 'energy':
        await animateEnergy({
          superview: this.root,
          start: { position: start, scale: 1 },
          count: this.reward.value,
        });
        break;
    }
  }
}
